import SwitchAccessShortcutIcon from '@mui/icons-material/SwitchAccessShortcut'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation } from 'urql'
import { drawMoney, useConfetti } from '../../contexts/ConfettiProvider'
import { useOrderContext } from '../../contexts/OrderContextProvider'
import { graphql } from '../../gql'
import { CreatePaymentInput } from '../../gql/graphql'
import { formatFormInputCurrencyString } from '../../utils/currencyFormatter'
import BigStatCurrency from '../BigStatCurrency'

const createPaymentMutation = graphql(`
  mutation AddPaymentForm($input: CreatePaymentInput!) {
    CreatePayment(input: $input) {
      id
    }
  }
`)

export function AddPaymentForm() {
  const { rfq, closeDialogs, refetch: refetchRfqQuery } = useOrderContext()
  const [success, setSuccess] = useState(false)
  const { showConfetti } = useConfetti()
  const [createPaymentResult, createPayment] = useMutation(createPaymentMutation)
  const purchaseOrderId = rfq?.quote?.purchaseOrder?.id

  useEffect(() => {
    if (success) {
      showConfetti({
        numberOfPieces: 200,
        initialVelocityY: -10,
        drawShape: drawMoney,
      })
      setTimeout(() => {
        closeDialogs()
        refetchRfqQuery()
      }, 1000)
    }
  }, [success, showConfetti, closeDialogs, refetchRfqQuery])

  interface IFormInput {
    amount: string
    dateReceived: Dayjs | undefined
    paidInFull: boolean
  }

  const {
    handleSubmit,
    control,
    setValue,
    formState,
    watch,
    trigger: triggerValidation,
  } = useForm<IFormInput>({
    defaultValues: {
      amount: '',
      dateReceived: dayjs(),
      paidInFull: false,
    },
    mode: 'onBlur',
    disabled: createPaymentResult.fetching,
  })
  const amountStr = watch('amount')
  const amount = parseNumStr(amountStr)

  const remainingCommissionDue = useMemo(() => {
    const po = rfq?.quote?.purchaseOrder
    if (!po) return
    const totalExistingPayments = po.payments.reduce((acc, p) => acc + p.amount, 0)
    const commissionDue = po.amount * (po.commissionPercentage / 100)
    return commissionDue - totalExistingPayments
  }, [rfq])

  const isPaymentCloseToFulfilled = useMemo(() => {
    const po = rfq?.quote?.purchaseOrder
    if (!po) return false
    if (!amount || Number.isNaN(amount)) return false
    const totalExistingPayments = po.payments.reduce((acc, p) => acc + p.amount, 0)
    const commissionDue = po.amount * (po.commissionPercentage / 100)
    const percentagePaid = (totalExistingPayments + amount) / commissionDue
    return percentagePaid >= 0.95 && percentagePaid <= 1.05
  }, [amount, rfq])

  useEffect(() => {
    if (isPaymentCloseToFulfilled !== undefined) {
      setValue('paidInFull', isPaymentCloseToFulfilled)
    }
  }, [isPaymentCloseToFulfilled, setValue])

  const handleAmountBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const formatted = formatFormInputCurrencyString(e.target.value)
    if (!formatted) return
    setValue('amount', formatted)
    triggerValidation('amount')
  }

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    if (createPaymentResult.fetching) return
    if (success) return
    if (!purchaseOrderId) return
    const input: CreatePaymentInput = {
      amount: parseNumStr(data.amount),
      dateReceived: data.dateReceived?.toISOString(),
      purchaseOrderId,
      paidInFull: data.paidInFull,
    }
    const resp = await createPayment({
      input,
    })
    if (!resp.error) {
      setSuccess(true)
    }
  }

  const $remainingCommissionDue = useMemo(() => {
    if (typeof remainingCommissionDue === 'undefined') return
    return (
      <Box display="flex" alignItems="center" flexDirection="row">
        <Typography variant="caption">Estimated remaining commission due:</Typography>
        <Tooltip title="Click to enter this amount in the payment field" arrow placement="top">
          <Button
            variant="text"
            endIcon={<SwitchAccessShortcutIcon fontSize="inherit" sx={{ rotate: '180deg' }} />}
            onClick={() => {
              const formatted = formatFormInputCurrencyString(remainingCommissionDue.toString())
              if (!formatted) return
              setValue('amount', formatted)
              triggerValidation('amount')
            }}
          >
            <BigStatCurrency sx={{ fontSize: 'small', ml: 1 / 2 }} value={remainingCommissionDue} color="success" />
          </Button>
        </Tooltip>
      </Box>
    )
  }, [remainingCommissionDue, setValue, triggerValidation])

  return (
    <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <Controller
          name="amount"
          control={control}
          rules={{
            required: true,
            validate: (v) => {
              if (!v) return 'Amount is required'
              const n = Number.parseFloat(v as any as string)
              if (Number.isNaN(n)) {
                return 'Amount must be a number'
              }
              return true
            },
            onBlur: handleAmountBlur,
          }}
          render={({ field, fieldState }) => {
            return (
              <>
                {$remainingCommissionDue}
                <TextField
                  label="Amount ($) *"
                  {...field}
                  // disabled={fetching}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || 'The payment amount received'}
                  slotProps={{
                    input: {
                      startAdornment: '$',
                      required: true,
                    },
                  }}
                />
              </>
            )
          }}
        />
        <Controller
          name="dateReceived"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <DatePicker
                label="Date Received"
                {...field}
                disableFuture
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message || 'When you received the payment',
                  },
                  field: {
                    clearable: true,
                  },
                }}
              />
            )
          }}
        />
        <Controller
          name="paidInFull"
          control={control}
          render={({ field }) => {
            return (
              <Box pb={4}>
                <FormControl>
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} aria-label="Paid in full?" color="success" />}
                    label="Paid in full?"
                  />
                </FormControl>

                <FormHelperText margin="dense">
                  If checked, the order's status will be changed to <code>paid-in-full</code>.
                </FormHelperText>
              </Box>
            )
          }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={!formState.isValid || createPaymentResult.fetching}
          color={success ? 'success' : 'primary'}
        >
          {(() => {
            if (success) {
              return 'Payment Added!'
            }
            if (createPaymentResult.fetching) {
              return 'Adding Payment...'
            }
            return 'Add Payment'
          })()}
        </Button>
      </Stack>
    </form>
  )
}

const parseNumStr = (str: string) => {
  return Number.parseFloat(str.replace(/[^0-9.]/g, ''))
}
